import {
  buildEntitySearchCall,
  filterManagersByQuery,
  pickListItems,
} from './entitySmartSearchLogic.mjs'

/**
 * Кэш списка менеджеров. Ключ различает скоуп:
 *   'all'              — глобальный список (без company_id), для поиска по тексту
 *   'company:<id>'     — список менеджеров конкретной компании
 */
const managersCache = new Map()
const managersCachePromise = new Map()

function coerceListResponse(data, itemsKey) {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (Array.isArray(data.items)) return data.items
  if (itemsKey && Array.isArray(data[itemsKey])) return data[itemsKey]
  if (Array.isArray(data.managers)) return data.managers
  if (Array.isArray(data.data)) return data.data
  return []
}

/**
 * Ключ кеша для менеджеров. Пустой/непустой query — разные скоупы:
 *   • непустой query  → 'all' (глобальный список; локальная фильтрация в filterManagersByQuery)
 *   • пустой query    → 'company:<companyId>' если companyId задан, иначе 'all'
 */
function managersCacheKey({ query, companyId }) {
  if (String(query || '').trim() && companyId != null && companyId !== '') {
    return 'all'
  }
  if (companyId != null && companyId !== '') {
    return `company:${companyId}`
  }
  return 'all'
}

export function invalidateManagersCache() {
  managersCache.clear()
  managersCachePromise.clear()
}

/**
 * @param {object} opts
 * @param {AbortSignal} [opts.signal]
 * @param {Function} opts.request — (path, params, { signal }) => Promise<data>
 * @param {(number|string)} [opts.companyId] — если задан И query пустой, добавляется
 *   company_id в запрос (фильтр по компании на бэке). При непустом query игнорируется,
 *   т.к. поиск по тексту ведётся глобально.
 * @param {string} [opts.query] — текущий запрос пользователя (для выбора скоупа кеша).
 */
async function loadManagers({ signal, request, companyId, query }) {
  const key = managersCacheKey({ query, companyId })
  if (managersCache.has(key)) return managersCache.get(key)
  if (!managersCachePromise.has(key)) {
    const params = { page: 1, per_page: 100 }
    const trimmed = String(query || '').trim()
    // Только пустой запрос сужаем до текущей компании; набор текста → глобально.
    if (!trimmed && companyId != null && companyId !== '') {
      params.company_id = companyId
    }
    // общий fetch без caller's signal — иначе abort одного запроса роняет кэш для всех
    const basePromise = request('managers/list', params, {})
      .then((data) => coerceListResponse(data, 'managers'))
    const promise = basePromise
      .then((list) => {
        const safe = Array.isArray(list) ? list : []
        managersCache.set(key, safe)
        return safe
      })
      .catch((err) => {
        managersCache.delete(key)
        throw err
      })
      .finally(() => {
        managersCachePromise.delete(key)
      })
    managersCachePromise.set(key, promise)
  }
  if (signal) {
    return new Promise((resolve, reject) => {
      const onAbort = () => reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }))
      if (signal.aborted) {
        onAbort()
        return
      }
      signal.addEventListener('abort', onAbort, { once: true })
      managersCachePromise.get(key).then(
        (value) => {
          signal.removeEventListener('abort', onAbort)
          resolve(value)
        },
        (err) => {
          signal.removeEventListener('abort', onAbort)
          reject(err)
        },
      )
    })
  }
  return managersCachePromise.get(key)
}

/**
 * @param {string} model
 * @param {string} query
 * @param {object} options
 * @param {AbortSignal} [options.signal]
 * @param {Function} options.request — (path, params, { signal }) => Promise<data>
 * @param {(number|string)} [options.companyId] — для менеджеров с пустым query
 *   фильтрует список по текущей компании; при непустом query игнорируется (глобальный поиск).
 */
export async function searchEntitySuggestions(model, query, options = {}) {
  const { signal, request, companyId } = options
  if (typeof request !== 'function') {
    throw new Error('searchEntitySuggestions requires options.request')
  }

  const call = buildEntitySearchCall(model, query, { companyId })
  if (!call) return []

  if (call.kind === 'list') {
    const data = await request(call.path, call.params, { signal })
    return pickListItems(call, data)
  }

  if (call.kind === 'managers') {
    const list = await loadManagers({ signal, request, companyId, query: call.query })
    return filterManagersByQuery(list, call.query)
  }

  return []
}
